/* =============================================================================
   DAILE Mono · commerce group (2026-10-06). No dependencies; loaded (defer) by the
   main section of each commerce page through snippets/mn-com-assets.liquid.

   1. Cart page (sections/mn-com-cart.liquid): AJAX quantity / remove / add-ons /
      note, then the section is re-rendered by Shopify (Section Rendering API) —
      the markup has one source, the Liquid. Logic as assets/daile-cart.js:
      add-ons follow their artwork (_parent_line → _line_id, fallback key).
   2. Free-shipping line in the visitor's currency (Shopify.currency.rate).
   3. Artwork viewer (Digital Art Story): full-screen <dialog>, zoom buttons,
      wheel / pinch, drag, double tap, keys + - 0 and arrows (as dv-microbook.js).
   4. Track order: the ParcelPanel widget of /apps/parcelpanel mounted in the page.
   5. Form status focus (contact), theme editor re-init.
   ============================================================================= */
(() => {
  'use strict';

  if (window.MNCOM) return;
  window.MNCOM = { version: '1.0.0' };

  const d = document;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const shopRoot = (() => {
    const r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.endsWith('/') ? r : `${r}/`;
  })();

  const money = (cents, currency) => {
    const code = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat((window.Shopify && window.Shopify.locale) || undefined, {
        style: 'currency', currency: code, numberingSystem: 'latn', minimumFractionDigits: 0, maximumFractionDigits: 2,
      }).format((cents || 0) / 100);
    } catch (e) {
      return `${((cents || 0) / 100).toFixed(2)} ${code}`;
    }
  };

  /* Polite announcements (one live region for the page). */
  let live = null;
  const announce = (text) => {
    if (!text) return;
    if (!live) {
      live = d.createElement('p');
      live.className = 'mn-sr-only';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      d.body.appendChild(live);
    }
    live.textContent = '';
    window.setTimeout(() => { live.textContent = text; }, 60);
  };

  /* 1. Cart ================================================================== */

  const readJSON = (r) => r.json().catch(() => null).then((data) => {
    if (!r.ok) throw data || new Error(`HTTP ${r.status}`);
    return data;
  });
  const post = (path, body) => fetch(shopRoot + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then(readJSON);
  const getCart = () => fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(readJSON);
  const lineId = (item) => (item.properties && item.properties._line_id) || item.key;

  const cartRoot = () => d.querySelector('[data-mn-com-cart]');
  let busy = false;
  let noteTimer = 0;

  const setCount = (n) => {
    d.querySelectorAll('cart-count').forEach((el) => { el.textContent = String(n); });
  };

  // Re-render the section in place (keeps ?view=… so the Mono template answers).
  const rerender = async (focusSelector) => {
    const current = cartRoot();
    if (!current) return;
    const id = current.getAttribute('data-section-id');
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('section_id', id);
    let fresh = null;
    try {
      const html = await fetch(url.toString(), { cache: 'no-store', headers: { Accept: 'text/html' } }).then((r) => (r.ok ? r.text() : ''));
      fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mn-com-cart]');
    } catch (e) { fresh = null; }
    if (!fresh) {
      // Fallback: the whole page (Section Rendering API unavailable).
      url.searchParams.delete('section_id');
      const html = await fetch(url.toString(), { cache: 'no-store' }).then((r) => r.text());
      fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mn-com-cart]');
    }
    if (!fresh) { window.location.reload(); return; }
    // The inline drawer guard inside the section must not run again; the stylesheet stays.
    current.replaceWith(fresh);
    initShipping(fresh);
    initBar(fresh);
    initPreviews(fresh);
    setCount(parseInt(fresh.getAttribute('data-count'), 10) || 0);
    const target = (focusSelector && fresh.querySelector(focusSelector)) || fresh.querySelector('.mn-com-cart__title');
    if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    const total = fresh.querySelector('[data-mn-com-total] span');
    announce(total ? `${fresh.getAttribute('data-t-subtotal') || ''}: ${total.textContent.trim()}` : fresh.querySelector('.mn-com-cart__title').textContent.trim());
  };

  const mutate = async (operation, focusSelector) => {
    const root = cartRoot();
    if (!root || busy) return;
    busy = true;
    root.classList.add('is-busy');
    root.setAttribute('aria-busy', 'true');
    let failed = null;
    try {
      await operation();
    } catch (error) {
      failed = error;
    }
    try {
      await rerender(focusSelector);
    } catch (e) {
      window.location.reload();
      return;
    } finally {
      busy = false;
      const now = cartRoot();
      if (now) { now.classList.remove('is-busy'); now.removeAttribute('aria-busy'); }
    }
    if (failed) {
      const now = cartRoot();
      const box = now && now.querySelector('[data-mn-com-cart-error]');
      const msg = (failed && typeof failed.description === 'string' && failed.description) || (now && now.getAttribute('data-t-error')) || 'Error';
      if (box) { box.textContent = msg; box.hidden = false; }
      announce(msg);
    }
  };

  // New quantity for an artwork; its add-ons follow when they had the same quantity (one glue kit per puzzle).
  const setQuantity = (key, qty, dir) => mutate(async () => {
    const cart = await getCart();
    const main = cart.items.find((it) => it.key === key);
    if (!main) return;
    const pid = lineId(main);
    const updates = { [key]: qty };
    cart.items.forEach((it) => {
      if (it.key === key || !it.properties || it.properties._parent_line !== pid) return;
      if (qty === 0 || it.quantity === main.quantity) updates[it.key] = qty;
    });
    await post('cart/update.js', { updates });
  }, qty > 0 ? `[data-key="${CSS.escape(key)}"] [data-mn-com-qty][data-dir="${dir || 'inc'}"]` : null);

  const removeChild = (key) => mutate(() => post('cart/change.js', { id: key, quantity: 0 }));

  const addOffer = (btn) => mutate(() => post('cart/add.js', {
    items: [{
      id: Number(btn.getAttribute('data-variant')),
      quantity: Math.max(1, parseInt(btn.getAttribute('data-qty'), 10) || 1),
      properties: { _parent_line: btn.getAttribute('data-parent'), _set: btn.getAttribute('data-set') || '' },
    }],
  }));

  const saveNote = (value) => post('cart/update.js', { note: value }).catch(() => { /* kept in the form for checkout */ });

  d.addEventListener('click', (e) => {
    const root = e.target.closest('[data-mn-com-cart]');
    if (!root) {
      // Header cart button on the cart page: stay here.
      const opener = cartRoot() && e.target.closest('[data-daile-cart-open]');
      if (opener) {
        e.preventDefault();
        const title = cartRoot().querySelector('.mn-com-cart__title');
        window.scrollTo({ top: 0, behavior: reduce.matches ? 'auto' : 'smooth' });
        if (title) title.focus({ preventScroll: true });
      }
      return;
    }
    const qtyBtn = e.target.closest('[data-mn-com-qty]');
    if (qtyBtn) {
      e.preventDefault();
      if (qtyBtn.disabled) return;
      setQuantity(qtyBtn.getAttribute('data-key'), Math.max(0, parseInt(qtyBtn.getAttribute('data-qty'), 10) || 0), qtyBtn.getAttribute('data-dir'));
      return;
    }
    const remove = e.target.closest('[data-mn-com-remove]');
    if (remove) {
      e.preventDefault();
      setQuantity(remove.getAttribute('data-key'), 0);
      return;
    }
    const childRemove = e.target.closest('[data-mn-com-child-remove]');
    if (childRemove) {
      e.preventDefault();
      removeChild(childRemove.getAttribute('data-key'));
      return;
    }
    const offer = e.target.closest('[data-mn-com-offer]');
    if (offer) {
      e.preventDefault();
      addOffer(offer);
    }
  });

  d.addEventListener('input', (e) => {
    const note = e.target.closest && e.target.closest('[data-mn-com-note]');
    if (!note) return;
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(() => saveNote(note.value), 600);
  });

  // Checkout: send the note first if it is still waiting (the form also carries it).
  d.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.closest || !form.closest('[data-mn-com-cart]')) return;
    const submitter = e.submitter;
    if (submitter && submitter.hasAttribute('data-mn-com-qty')) {
      e.preventDefault();
      return;
    }
    window.clearTimeout(noteTimer);
  });

  // Sticky checkout bar: hidden while the summary (with its own button) is on screen.
  let barObserver = null;
  function initBar(scope) {
    const root = (scope && scope.matches && scope.matches('[data-mn-com-cart]')) ? scope : (scope || d).querySelector('[data-mn-com-cart]');
    if (!root) return;
    const bar = root.querySelector('[data-mn-com-bar]');
    const sum = root.querySelector('.mn-com-sum');
    if (barObserver) barObserver.disconnect();
    if (!bar || !sum || !('IntersectionObserver' in window)) return;
    barObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => bar.classList.toggle('is-hidden', entry.isIntersecting || entry.boundingClientRect.top < 0));
    }, { threshold: 0 });
    barObserver.observe(sum);
  }

  // Photo-puzzle thumbnails: this session's local preview first (as the drawer), product photo if the CDN file fails.
  function initPreviews(scope) {
    (scope || d).querySelectorAll('img[data-mn-com-preview]').forEach((img) => {
      let local = null;
      try { local = window.sessionStorage.getItem(`daile_cz_preview_${img.getAttribute('data-mn-com-preview')}`); } catch (e) { local = null; }
      if (local && /^(data:image\/|blob:|https:)/.test(local)) { img.src = local; return; }
      // The file failed before this script ran (no error event left to catch).
      if (img.complete && !img.naturalWidth) {
        const fallback = img.getAttribute('data-fallback');
        if (fallback) img.src = fallback; else img.style.visibility = 'hidden';
      }
    });
  }
  d.addEventListener('error', (e) => {
    const img = e.target;
    if (!(img instanceof HTMLImageElement) || !img.hasAttribute('data-mn-com-preview')) return;
    const fallback = img.getAttribute('data-fallback');
    if (fallback && img.getAttribute('src') !== fallback) img.src = fallback;
    else img.style.visibility = 'hidden';
  }, true);

  /* 2. Free-shipping line ===================================================== */

  function initShipping(scope) {
    (scope || d).querySelectorAll('[data-mn-com-ship]').forEach((bar) => {
      const base = bar.getAttribute('data-base');
      const currency = bar.getAttribute('data-currency');
      if (bar.getAttribute('data-free') === 'true' || !currency || currency === base) return; // Liquid did it
      const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      if (!(rate > 0)) return; // keep "Free shipping over €100"
      const threshold = Math.round((parseInt(bar.getAttribute('data-threshold'), 10) || 0) * rate);
      const total = parseInt(bar.getAttribute('data-total'), 10) || 0;
      if (threshold <= 0) return;
      const textEl = bar.querySelector('[data-mn-com-ship-text] span');
      const fill = bar.querySelector('[data-mn-com-ship-fill]');
      const reached = total >= threshold;
      bar.classList.toggle('is-reached', reached);
      if (fill) fill.style.setProperty('--mn-ship', `${reached ? 100 : Math.min(99, Math.round((total / threshold) * 100))}%`);
      if (!textEl) return;
      if (reached) {
        textEl.textContent = bar.getAttribute('data-t-ok') || '';
      } else {
        textEl.textContent = '';
        const add = (bar.getAttribute('data-t-add') || '').trim();
        const forFree = (bar.getAttribute('data-t-for') || '').trim();
        const amount = d.createElement('bdi');
        amount.textContent = money(threshold - total, currency);
        if (add) textEl.append(`${add} `);
        textEl.append(amount);
        if (forFree) textEl.append(` ${forFree}`);
      }
    });
  }

  /* 3. Artwork viewer ========================================================= */

  function initViewer(scope) {
    (scope || d).querySelectorAll('[data-mn-com-viewer]').forEach((dialog) => {
      if (dialog.hasAttribute('data-mn-com-ready')) return;
      dialog.setAttribute('data-mn-com-ready', '');
      const stage = dialog.querySelector('[data-viewer-stage]');
      const img = dialog.querySelector('[data-viewer-img]');
      const btnIn = dialog.querySelector('[data-viewer-in]');
      const btnOut = dialog.querySelector('[data-viewer-out]');
      const btnFit = dialog.querySelector('[data-viewer-fit]');
      if (!stage || !img || typeof dialog.showModal !== 'function') return;

      let scale = 1;
      let tx = 0;
      let ty = 0;
      let baseW = 0;
      let baseH = 0;
      let maxScale = 4;
      let opener = null;
      const pointers = new Map();
      let drag = null;
      let pinch = null;
      let lastTap = 0;

      const clampPan = () => {
        const r = stage.getBoundingClientRect();
        const mx = Math.max(0, (baseW * scale - r.width) / 2);
        const my = Math.max(0, (baseH * scale - r.height) / 2);
        tx = Math.min(mx, Math.max(-mx, tx));
        ty = Math.min(my, Math.max(-my, ty));
      };
      const apply = (animate) => {
        clampPan();
        stage.classList.toggle('is-animating', !!animate && !reduce.matches);
        img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
        if (btnIn) btnIn.disabled = scale >= maxScale - 0.001;
        if (btnOut) btnOut.disabled = scale <= 1.001;
        if (btnFit) btnFit.disabled = scale <= 1.001 && tx === 0 && ty === 0;
      };
      const fit = () => {
        const r = stage.getBoundingClientRect();
        const natW = img.naturalWidth || Number(img.getAttribute('width')) || 1;
        const natH = img.naturalHeight || Number(img.getAttribute('height')) || 1;
        const pad = r.width < 750 ? 16 : 72;
        const bottom = r.width < 750 ? 96 : 110;
        const f = Math.min((r.width - pad * 2) / natW, (r.height - pad - bottom) / natH);
        baseW = Math.max(1, natW * f);
        baseH = Math.max(1, natH * f);
        img.style.width = `${baseW}px`;
        img.style.height = `${baseH}px`;
        const fileW = Number(img.getAttribute('width')) || natW;
        maxScale = Math.min(8, Math.max(4, (Math.min(fileW, 3200) / baseW) * 1.5));
      };
      const reset = (animate) => { scale = 1; tx = 0; ty = 0; apply(animate); };
      const zoomAt = (next, px, py, animate) => {
        const target = Math.min(maxScale, Math.max(1, next));
        const ratio = target / scale;
        tx = px - (px - tx) * ratio;
        ty = py - (py - ty) * ratio;
        scale = target;
        apply(animate);
      };
      const centre = (x, y) => {
        const r = stage.getBoundingClientRect();
        return [x - (r.left + r.width / 2), y - (r.top + r.height / 2)];
      };
      const loadFull = () => {
        const full = img.getAttribute('data-src');
        if (!full || img.getAttribute('src') === full) return;
        const hi = new Image();
        hi.decoding = 'async';
        hi.onload = () => { img.src = full; fit(); apply(false); };
        hi.src = full;
      };

      const open = (trigger) => {
        opener = trigger || d.activeElement;
        dialog.showModal();
        d.documentElement.classList.add('mn-lock');
        img.loading = 'eager';
        const ready = () => { fit(); reset(false); };
        if (img.complete && img.naturalWidth) ready();
        else img.addEventListener('load', ready, { once: true });
        requestAnimationFrame(ready);
        loadFull();
      };

      d.querySelectorAll(`[data-mn-com-viewer-open="${dialog.id}"]`).forEach((btn) => {
        btn.addEventListener('click', (e) => { e.preventDefault(); open(btn); });
      });
      dialog.querySelectorAll('[data-viewer-close]').forEach((btn) => btn.addEventListener('click', () => dialog.close()));
      dialog.addEventListener('close', () => {
        pointers.clear();
        drag = null;
        pinch = null;
        stage.classList.remove('is-dragging');
        reset(false);
        d.documentElement.classList.remove('mn-lock');
        if (opener && opener.focus) opener.focus({ preventScroll: true });
      });
      stage.addEventListener('wheel', (e) => {
        e.preventDefault();
        const [px, py] = centre(e.clientX, e.clientY);
        zoomAt(scale * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0022)), px, py, false);
      }, { passive: false });
      stage.addEventListener('pointerdown', (e) => {
        if (e.button !== undefined && e.button > 0) return;
        stage.setPointerCapture(e.pointerId);
        pointers.set(e.pointerId, [e.clientX, e.clientY]);
        if (pointers.size === 1) {
          drag = { x: e.clientX - tx, y: e.clientY - ty };
          stage.classList.add('is-dragging');
          const now = Date.now();
          if (e.pointerType !== 'mouse' && now - lastTap < 300) {
            const [px, py] = centre(e.clientX, e.clientY);
            if (scale > 1.01) reset(true); else zoomAt(2.5, px, py, true);
            lastTap = 0;
          } else lastTap = now;
        } else if (pointers.size === 2) {
          const [a, b] = [...pointers.values()];
          const [mx, my] = centre((a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
          pinch = { dist: Math.hypot(a[0] - b[0], a[1] - b[1]) || 1, scale, mx, my, tx, ty };
          drag = null;
        }
      });
      stage.addEventListener('pointermove', (e) => {
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, [e.clientX, e.clientY]);
        if (pinch && pointers.size >= 2) {
          const [a, b] = [...pointers.values()];
          const dist = Math.hypot(a[0] - b[0], a[1] - b[1]) || 1;
          const [mx, my] = centre((a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
          const target = Math.min(maxScale, Math.max(1, pinch.scale * (dist / pinch.dist)));
          const ratio = target / pinch.scale;
          scale = target;
          tx = mx - (pinch.mx - pinch.tx) * ratio;
          ty = my - (pinch.my - pinch.ty) * ratio;
          apply(false);
        } else if (drag) {
          tx = e.clientX - drag.x;
          ty = e.clientY - drag.y;
          apply(false);
        }
      });
      const up = (e) => {
        pointers.delete(e.pointerId);
        if (pointers.size < 2) pinch = null;
        if (pointers.size === 1) { const [p] = [...pointers.values()]; drag = { x: p[0] - tx, y: p[1] - ty }; }
        if (pointers.size === 0) { drag = null; stage.classList.remove('is-dragging'); }
      };
      stage.addEventListener('pointerup', up);
      stage.addEventListener('pointercancel', up);
      stage.addEventListener('dblclick', (e) => {
        const [px, py] = centre(e.clientX, e.clientY);
        if (scale > 1.01) reset(true); else zoomAt(2.5, px, py, true);
      });
      dialog.addEventListener('keydown', (e) => {
        const step = 60;
        switch (e.key) {
          case '+': case '=': zoomAt(scale * 1.4, 0, 0, true); break;
          case '-': case '_': zoomAt(scale / 1.4, 0, 0, true); break;
          case '0': reset(true); break;
          case 'ArrowLeft': if (scale <= 1.001) return; tx += step; apply(true); break;
          case 'ArrowRight': if (scale <= 1.001) return; tx -= step; apply(true); break;
          case 'ArrowUp': if (scale <= 1.001) return; ty += step; apply(true); break;
          case 'ArrowDown': if (scale <= 1.001) return; ty -= step; apply(true); break;
          default: return;
        }
        e.preventDefault();
      });
      if (btnIn) btnIn.addEventListener('click', () => zoomAt(scale * 1.4, 0, 0, true));
      if (btnOut) btnOut.addEventListener('click', () => zoomAt(scale / 1.4, 0, 0, true));
      if (btnFit) btnFit.addEventListener('click', () => reset(true));
      window.addEventListener('resize', () => {
        if (!dialog.open) return;
        const wasFit = scale <= 1.001;
        fit();
        if (wasFit) reset(false); else apply(false);
      });
    });
  }

  /* 4. Track order: ParcelPanel ================================================ */

  /* The app proxy page /apps/parcelpanel renders the widget inside the store layout:
     a config <script>, <div id="pp-tracking-page-app"> and the app bundle. The page is
     fetched (same origin, the visitor's language prefix), the <main> content is moved
     here and its scripts are re-created so they run. The widget then works in place
     (it writes ?order= / ?nums= to this page's address). Without JS, or if anything
     fails, the link to /apps/parcelpanel stays. */
  function initTrack(scope) {
    (scope || d).querySelectorAll('[data-mn-com-track]').forEach(async (host) => {
      if (host.hasAttribute('data-mn-com-ready')) return;
      host.setAttribute('data-mn-com-ready', '');
      const mount = host.querySelector('[data-mn-com-track-mount]');
      const fallback = host.querySelector('[data-mn-com-track-fallback]');
      const loading = host.querySelector('[data-mn-com-track-loading]');
      const src = host.getAttribute('data-src') || `${shopRoot}apps/parcelpanel`;
      if (!mount || d.getElementById('pp-tracking-page-app')) return;
      if (loading) loading.hidden = false;
      if (fallback) fallback.hidden = true;
      const fail = () => {
        if (loading) loading.hidden = true;
        if (fallback) fallback.hidden = false;
        mount.innerHTML = '';
      };
      try {
        const html = await fetch(src, { credentials: 'same-origin', headers: { Accept: 'text/html' } }).then((r) => (r.ok ? r.text() : ''));
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const app = doc.getElementById('pp-tracking-page-app');
        if (!app) { fail(); return; }
        const box = app.closest('main') || app.parentElement;
        const nodes = [...box.childNodes].filter((n) => n.nodeType === 1 && (n.matches('script, style, link[rel="stylesheet"]') || n.id === 'pp-tracking-page-app' || n.querySelector('#pp-tracking-page-app')));
        const scripts = [];
        nodes.forEach((n) => {
          if (n.tagName === 'SCRIPT') {
            scripts.push(n);
            return;
          }
          mount.appendChild(d.importNode(n, true));
        });
        // Scripts in order (the bundle needs the config objects first).
        for (const old of scripts) {
          const s = d.createElement('script');
          [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
          if (old.src) {
            await new Promise((resolve) => {
              s.onload = resolve;
              s.onerror = resolve;
              mount.appendChild(s);
            });
          } else {
            s.textContent = old.textContent;
            mount.appendChild(s);
          }
        }
        if (loading) loading.hidden = true;
        // The bundle renders asynchronously; if nothing appears, show the link again.
        window.setTimeout(() => {
          const el = d.getElementById('pp-tracking-page-app');
          if (!el || !el.children.length) fail();
        }, 8000);
      } catch (e) {
        fail();
      }
    });
  }

  /* 5. Form status focus ====================================================== */

  function initStatus(scope) {
    const status = (scope || d).querySelector('[data-mn-com-focus]');
    if (status) status.focus();
  }

  /* Init ======================================================================= */

  const init = (scope) => {
    initShipping(scope);
    initBar(scope);
    initPreviews(scope);
    initViewer(scope);
    initTrack(scope);
    initStatus(scope);
  };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', () => init(d));
  else init(d);
  d.addEventListener('shopify:section:load', (e) => init(e.target));
})();
